import React, { useRef } from "react";
import { ABOUT_CONTENT } from "../Constants/constants";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { useEvent } from "../hooks/useEvent";
import { ANALYTICS_EVENTS } from "../Constants/analyticsEvents";
import { scrollToSection } from "../lib/lenis";

const container = (delay) => ({
  hidden: { x: -70, opacity: 0 },
  visible: {
    x: 0,
    opacity: 1,
    transition: { duration: 0.5, delay: delay },
  },
});

const About = () => {
  const heroRef = useRef(null);
  const reduced = useReducedMotion();
  const track = useEvent();

  // As the hero scrolls away the name drifts up, spreads apart and blurs —
  // dispersing into the void the same way the video figure dissolves
  // between poses. letter-spacing is inherited, so one wrapper drives both
  // the name and the subtitle.
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const disperseOpacity = useTransform(scrollYProgress, [0, 0.75], [1, 0]);
  const disperseY = useTransform(scrollYProgress, [0, 1], [0, -90]);
  const disperseSpacing = useTransform(scrollYProgress, (v) => `${v * 0.35}em`);
  const disperseBlur = useTransform(scrollYProgress, (v) => `blur(${v * 7}px)`);

  return (
    <div className="pb-d lg:mb-35">
      <div
        ref={heroRef}
        className="relative flex flex-col items-center justify-center min-h-[50vh] lg:min-h-[70vh] py-12 text-center"
      >
        <motion.div
          style={
            reduced
              ? undefined
              : {
                  opacity: disperseOpacity,
                  y: disperseY,
                  letterSpacing: disperseSpacing,
                  filter: disperseBlur,
                }
          }
          className="flex flex-col items-center"
        >
          <motion.h1
            variants={container(0)}
            initial="hidden"
            animate="visible"
            className="relative z-10 lg:text-8xl pb-8 text-6xl font-thin tracking-tight text-slate-100 text-glow"
          >
            Oz Hayun
          </motion.h1>
          <motion.span
            variants={container(0.2)}
            initial="hidden"
            animate="visible"
            className="relative z-10 bg-gradient-to-r from-biolum-green via-slate-300 to-biolum-blue bg-clip-text text-3xl tracking-tight text-transparent font-bold"
          >
            AI Software Engineer
          </motion.span>
          <motion.div
            variants={container(0.35)}
            initial="hidden"
            animate="visible"
            className="relative z-10 flex flex-wrap items-center justify-center gap-4 mt-8"
          >
            <a
              href="/Oz_Hayun_AI_Engineer.pdf"
              download
              onClick={() => track(ANALYTICS_EVENTS.RESUME_DOWNLOAD_CLICK)}
              className="hover:bg-biolum-blue inline-block px-6 py-3 transition-colors bg-biolum-green text-ocean-dark font-bold rounded-lg shadow-[0_0_20px_rgba(100,255,218,0.4)]"
            >
              Download Resume
            </a>
            <button
              type="button"
              onClick={() => scrollToSection("projects", -80)}
              className="hover:border-biolum-green hover:text-biolum-green inline-block px-6 py-3 transition-colors border border-slate-400 text-slate-200 font-bold rounded-lg"
            >
              View Projects
            </button>
          </motion.div>
        </motion.div>
      </div>
      <motion.div
        variants={container(0.4)}
        initial="hidden"
        animate="visible"
        className="glass-card max-w-3xl mx-auto my-2 rounded-xl border-l-2 border-biolum-green/50 px-8 py-6"
      >
        <p className="text-start text-lg font-light tracking-tight whitespace-pre-line text-slate-200">
          {ABOUT_CONTENT}
        </p>
      </motion.div>
    </div>
  );
};

export default About;
